/**
 * Portfolio Data Configuration for Yeheskiel Pongrekun
 * Centralized content source of truth. Edit this file to update portfolio details,
 * experience, metrics, projects, skills, and contact links.
 */

export const portfolioData = {
  meta: {
    title: "Yeheskiel Pongrekun — Marketing & Communications Portfolio",
    description: "Marketing & Communications professional specializing in brand strategy, creator partnerships, and digital campaigns. Based in Bali, Indonesia.",
    author: "Yeheskiel Pongrekun",
    siteUrl: "https://yeheskiel.dev",
    keywords: [
      "Yeheskiel Pongrekun",
      "Marketing and Communications",
      "Brand Strategy",
      "Influencer Marketing",
      "KOL Management",
      "Creative Production",
      "Bali Hospitality Marketing",
      "Content Strategy"
    ]
  },

  personal: {
    name: "Yeheskiel Pongrekun",
    shortName: "Yeheskiel",
    initials: "YP",
    taglineBadge: "MARKETING & COMMUNICATIONS · PORTFOLIO",
    headlineLine1: "Ideas into",
    headlineLine2: "impact.",
    positioning: "Marketing & Communications",
    supportingExpertise: "Brand Strategy · Influencer Marketing · Creative Production",
    heroIntro: "I'm Yeheskiel Pongrekun — a marketing & communications professional connecting brands, creators, and audiences through strategy, storytelling, and meaningful collaborations.",
    heroCtas: {
      primary: { label: "Explore My Work", href: "#work" },
      secondary: { label: "Get In Touch", href: "#contact" }
    },
    about: {
      eyebrow: "01 / ABOUT ME",
      title: "Built for the creative side of business.",
      note: "Strategy-minded. People-focused. Comfortable where creativity meets operations.",
      copy: "I bring 3+ years of experience in marketing communications, influencer partnerships, content production, and brand development. From building a creator program from scratch to helping oversee multi-venue campaigns, I love turning ambitious ideas into organized, compelling experiences.",
      pillars: [
        "Hospitality and lifestyle marketing",
        "Brand strategy and brand refresh",
        "Influencer and KOL management",
        "Social media and digital marketing",
        "Content creation and creative production",
        "Cross-functional team coordination",
        "Marketing technology and digital workflows"
      ]
    }
  },

  metrics: [
    {
      id: "kols",
      value: "1,000+",
      label: "KOLs & creators collaborated with across markets"
    },
    {
      id: "venues",
      value: "10+",
      label: "Venues involved in a coordinated brand refresh"
    },
    {
      id: "bali-farm-house",
      value: "468+",
      label: "Creator collaborations at Bali Farm House over 14 months"
    },
    {
      id: "hospitality-freelance",
      value: "90+",
      label: "Hospitality venues covered through freelance content work"
    }
  ],

  projects: [
    {
      id: "kopindo",
      title: "One brand direction. Many distinct venues.",
      company: "Kopindo Jaya Group",
      role: "Assistant Manager Marketing Communications",
      tag: "BRAND STRATEGY · LEADERSHIP",
      graphicNum: "10+",
      graphicText: "KOPINDO JAYA GROUP",
      graphicBottom: "BRAND REFRESH",
      visualClass: "visual-kopindo",
      period: "July 2025 – February 2026",
      location: "Bali, Indonesia",
      badgeText: "10+ venues ↗",
      summary: "Helped direct a brand refresh for 10+ hospitality and lifestyle venues, aligning creative direction, visual identities, and digital messaging while supporting marketing operations.",
      responsibilities: [
        "Oversaw cross-functional marketing workflows, creative production, and social media deliverables",
        "Stepped into Marketing Manager responsibilities during leadership vacancy, leading campaigns and board presentations",
        "Coordinated brand refresh strategy across 10+ hospitality and lifestyle venues to ensure visual cohesion",
        "Managed external agency partnerships, PR initiatives, and influencer collaborations"
      ],
      results: [
        "Successfully unified 10+ distinct hospitality venues under consistent visual and messaging guidelines",
        "Streamlined inter-departmental creative workflows between operational teams, marketing, and design",
        "Maintained campaign velocity and board-level reporting without operational disruption"
      ]
    },
    {
      id: "bali-farm-house",
      title: "Building a creator program from day one.",
      company: "Bali Farm House",
      role: "Influencer Marketing Specialist (Founding Team)",
      tag: "INFLUENCER MARKETING · LAUNCH",
      graphicNum: "468+",
      graphicText: "BALI FARM HOUSE",
      graphicBottom: "CREATOR COLLABS",
      visualClass: "visual-bali",
      period: "June 2023 – August 2024",
      location: "Bali, Indonesia",
      badgeText: "468+ collabs ↗",
      summary: "Helped establish the influencer marketing function from the pre-opening stage, engaging 100+ creators for launch and coordinating 468+ collaborations over 14 months.",
      responsibilities: [
        "Formulated the pre-opening influencer marketing roadmap and creator engagement pipeline",
        "Recruited and managed 100+ high-engagement creators for the official pre-opening and launch campaigns",
        "Drafted clear Scope of Work (SOW) agreements, managed on-site hostings, and guided creative briefs",
        "Tracked post-campaign engagement, audience reach metrics, and ROI reporting"
      ],
      results: [
        "468+ creator collaborations executed seamlessly across 14 months",
        "Rapidly established Bali Farm House as a premier family & lifestyle destination in Bali",
        "Generated millions of organic impressions and viral social media traction"
      ]
    },
    {
      id: "bali-zoo",
      title: "Relationships that create reach.",
      company: "Bali Zoo & Flamingo Beach Club",
      role: "Influencer Marketing Specialist",
      tag: "KOL MANAGEMENT · PR SUPPORT",
      graphicNum: "1K+",
      graphicText: "BALI ZOO · FLAMINGO BEACH CLUB",
      graphicBottom: "CREATOR NETWORK",
      visualClass: "visual-zoo",
      period: "June 2023 – December 2023",
      location: "Bali, Indonesia",
      badgeText: "1,000+ creators ↗",
      summary: "Managed partnerships with 1,000+ local, national, and international influencers, coordinating creators, photoshoots, events, media requests, and performance reporting.",
      responsibilities: [
        "Built and maintained relations with a network of 1,000+ KOLs, travel creators, and lifestyle influencers",
        "Coordinated professional photography and videography productions on location",
        "Liaised with press, media crews, and VIP visitors for coverage and brand integrations",
        "Delivered comprehensive data-backed campaign analytics to senior stakeholders"
      ],
      results: [
        "Cultivated a vibrant multi-tier influencer roster across domestic and international markets",
        "Drove sustained visitor footfall and digital ticket sales inquiries through authentic creator advocacy",
        "Delivered consistent high-quality UGC asset bank for ongoing marketing channels"
      ]
    },
    {
      id: "freelance-content",
      title: "Making hospitality stories worth sharing.",
      company: "Freelance Content Creator & UGC",
      role: "Social Media Specialist & Content Creator",
      tag: "CONTENT STRATEGY · STORYTELLING",
      graphicNum: "90+",
      graphicText: "FREELANCE · CONTENT & UGC",
      graphicBottom: "VENUES FEATURED",
      visualClass: "visual-content",
      period: "January 2025 – June 2025",
      location: "Bali, Indonesia",
      badgeText: "90+ venues ↗",
      summary: "Created food and hospitality content across 90+ venues in six months, planned 60+ monthly posts, and partnered with 10+ fine dining restaurants for events and activations.",
      responsibilities: [
        "Directed on-site food, cocktail, and venue photography & short-form video production",
        "Curated content calendars with 60+ scheduled posts monthly, pairing engaging captions with strategic audio",
        "Partnered closely with culinary chefs, venue owners, and PR teams to highlight unique dining experiences",
        "Produced high-converting UGC assets for hospitality ad campaigns"
      ],
      results: [
        "Documented and promoted 90+ top-tier venues across Bali's competitive dining landscape in 6 months",
        "Secured partnerships and curated activations for 10+ premium fine dining establishments",
        "Achieved above-benchmark engagement rates on Instagram Reels and TikTok"
      ]
    }
  ],

  experience: [
    {
      period: "JUL 2025 — FEB 2026",
      role: "Assistant Manager, Marketing Communications",
      company: "Kopindo Jaya Group",
      location: "Bali, Indonesia",
      description: "Oversaw cross-functional marketing workflows, creative production, influencer and PR efforts, campaign delivery, and board updates; stepped into Marketing Manager responsibilities during a leadership vacancy."
    },
    {
      period: "JAN — JUN 2025",
      role: "Social Media Specialist & Content Creator",
      company: "Freelance Content Creator & UGC",
      location: "Bali, Indonesia",
      description: "Produced hospitality-led visual storytelling, built content calendars, wrote brand-aligned copy, and worked with fine dining venues on coverage and activations."
    },
    {
      period: "JUN 2023 — AUG 2024",
      role: "Influencer Marketing Specialist",
      company: "Bali Farm House",
      location: "Bali, Indonesia",
      description: "Founding team member for influencer marketing, from pre-opening creator strategy and launch activities to ongoing partnerships, production support, and reporting."
    },
    {
      period: "JUN — DEC 2023",
      role: "Influencer Marketing Specialist",
      company: "Bali Zoo & Flamingo Beach Club",
      location: "Bali, Indonesia",
      description: "Coordinated KOL, model, and media collaborations across two brands, including photoshoots, SOW agreements, reporting, and PR coordination."
    },
    {
      period: "OCT 2020 — MAR 2021",
      role: "Food & Beverage Internship Trainee",
      company: "Vakkaru Maldives",
      location: "Maldives",
      description: "Developed a strong foundation in premium hospitality standards, guest experience, and cross-cultural teamwork."
    }
  ],

  skills: [
    {
      icon: "✳",
      title: "Marketing & Brand Strategy",
      items: [
        "Marketing Communications",
        "Brand Strategy",
        "Campaign Execution",
        "Public Relations",
        "Social Media Strategy",
        "Multi-Brand Alignment"
      ]
    },
    {
      icon: "↗",
      title: "Influencer & Creator Relations",
      items: [
        "KOL Management",
        "Creator Partnerships",
        "Engagement Reporting",
        "Creator Vetting",
        "Stakeholder Management",
        "SOW Agreements"
      ]
    },
    {
      icon: "◈",
      title: "Content & Production",
      items: [
        "Photography",
        "Videography",
        "Copywriting",
        "UGC Content",
        "Storyboarding",
        "Creative Direction",
        "Visual Storytelling"
      ]
    },
    {
      icon: "⌘",
      title: "Digital Tools & Leadership",
      items: [
        "Meta / Google / TikTok Ads",
        "Canva",
        "Metricool",
        "Xiaohongshu (RED)",
        "Project Management",
        "Workflow Automation",
        "Web & App Development"
      ]
    }
  ],

  certifications: [
    "Indonesian International Digital Innovation Summit",
    "Virtual Assistant Certification",
    "Gratyo University — Business Class",
    "Digital Marketing Masterclass — BI & Blibli",
    "Photography & Videography — Bank Indonesia"
  ],

  languages: "Bahasa Indonesia (Native) · English (Professional)",

  contact: {
    email: "y.pongrekun@gmail.com",
    emailLink: "mailto:y.pongrekun@gmail.com?subject=Let%27s%20Connect",
    whatsapp: "+62 821-9744-1625",
    whatsappLink: "https://wa.me/6282197441625",
    instagram: "https://instagram.com/exellntlife",
    instagramHandle: "@exellntlife",
    location: "Bali, Indonesia",
    heading: "Have an idea?",
    subheading: "Let's connect. ↗",
    description: "Whether it's a brand collaboration, a marketing opportunity, or a great creative challenge — I'd love to hear from you."
  },

  tickerItems: [
    "BRAND STRATEGY",
    "INFLUENCER MARKETING",
    "CREATIVE PRODUCTION",
    "DIGITAL CAMPAIGNS",
    "BRAND REFRESH",
    "KOL MANAGEMENT"
  ]
};

